import React, { useEffect, useRef } from "react";
import { Avatar, Box, Divider, Skeleton, SxProps, Tooltip, Typography } from "@mui/material";
import { ReactComponent as SpinnerSvg } from "@/assets/icons/common/spinner.svg";
import { IChat, IGetChangeRequest } from "@/Washouts/types/PayrollClosing/queries";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { TAttachmentType } from "@/Washouts/types/WashoutReview";
import GetIconByAttachmentType from "../GetIconByAttachmentType";
import { resolveChatRequiredParameters } from "@/Washouts/helpers/WashoutReview";
import WashoutDialog from "./washoutDialog";
import { useQuery } from "@tanstack/react-query";
import { getChatAttachmentsPresignedUrl } from "@/Washouts/api/WashoutReview/queries";
import { faces, hands, miscellaneous } from "./emojis";

const checkIfOnlyEmoji = (message: string) => {
	const emojiArray = Array.from(message);
	return emojiArray.every((char) => faces.includes(char) || hands.includes(char) || miscellaneous.includes(char));
};

const MessageSkeleton = () => {
	return (
		<Box sx={{ display: "flex", alignItems: "flex-start", paddingTop: ".5rem" }}>
			<Skeleton
				variant="rectangular"
				sx={{ borderRadius: ".375rem", marginLeft: "auto", marginRight: ".375rem" }}
				width="60%"
				height="3rem"
			/>
			<Skeleton variant="circular">
				<Avatar sx={{ height: "1.875rem", width: "1.875rem" }} />
			</Skeleton>
		</Box>
	);
};

const TooltipAvatar = ({ name = "", sx, src = "" }: { name: string; sx: SxProps; src: string }) => {
	return (
		<Tooltip title={<Typography className="!font-sans">{name}</Typography>} placement="top" arrow>
			<Avatar src={src} sx={sx} />
		</Tooltip>
	);
};

type TMessagesProps = {
	selectedRowId: string | null;
	data?: IChat[];
	alertMessage: string;
	attachedFilesLength: number;
	openPopper: boolean;
	hideOpenWashout: boolean;
	userId: string;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	position: string;
	canEditWashout: boolean;
	selectedRow: IGetChangeRequest | null;
	pending: { isPending: boolean; mutationAction: string };
};

const ChatMessages = (props: TMessagesProps) => {
	const {
		selectedRowId,
		data,
		alertMessage,
		openPopper,
		attachedFilesLength,
		hideOpenWashout,
		userId,
		locationId,
		commissionCycleId,
		payPeriodId,
		position,
		pending,
		canEditWashout,
		selectedRow,
	} = props;
	const user = useSelector((state: RootState) => state.user.user);
	const messagesEndRef = useRef<HTMLDivElement | null>(null);

	const showMessageSkeleton = pending.isPending && pending.mutationAction === "SEND_MESSAGE";

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [showMessageSkeleton, data]);

	return (
		<Box className="flex-grow w-[100%] bg-white p-[20px] overflow-y-auto">
			{data?.map((msg, index) => {
				const {
					isCurrentUser,
					isNextMessageFromSameUserAndTime,
					borderRadius,
					isFirstMessageFromCurrentUser,
					isFirstMessageFromNotCurrentUser,
					formattedDate,
				} = resolveChatRequiredParameters({ data, msg, user, index });
				const isOnlyEmoji = checkIfOnlyEmoji(msg.message);
				const messageBoxSx = isOnlyEmoji ? {} : { borderRadius };
				const messageBoxClasses = isOnlyEmoji
					? [!isFirstMessageFromCurrentUser ? "!mr-[35px]" : "", !isFirstMessageFromNotCurrentUser ? "!ml-[35px]" : ""].join(" ")
					: [
							"p-3",
							"max-w-[75%]",
							isCurrentUser ? "bg-primary text-white" : "bg-table-hover text-default",
							isNextMessageFromSameUserAndTime ? "mb-1" : "",
							!isFirstMessageFromCurrentUser ? "!mr-[35px]" : "",
							!isFirstMessageFromNotCurrentUser ? "!ml-[35px]" : "",
					  ].join(" ");
				const messageTextClasses = isOnlyEmoji ? "mb-2 !font-sans !text-4xl" : "mb-2 !font-sans !break-words";
				return (
					<React.Fragment key={msg._id}>
						<Box className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}>
							{isFirstMessageFromNotCurrentUser && (
								<TooltipAvatar
									name={msg?.createdBy?.fullName}
									src={msg?.createdBy?.presignedProfilePicture}
									sx={{ width: 30, height: 30, marginRight: "5px" }}
								/>
							)}
							<Box sx={messageBoxSx} className={messageBoxClasses}>
								{msg.message && (
									<Typography variant="body1" className={messageTextClasses}>
										{msg.message}
									</Typography>
								)}
								{msg.attachments.length > 0 && (
									<Box>
										{msg.attachments.map((attachment) => (
											<Attachments
												key={attachment._id}
												attachment={attachment}
												locationId={locationId}
												commissionCycleId={commissionCycleId}
												payPeriodId={payPeriodId}
												requestId={selectedRowId}
											/>
										))}
									</Box>
								)}
							</Box>
							{isFirstMessageFromCurrentUser && (
								<TooltipAvatar
									name={msg?.createdBy?.fullName}
									src={msg?.createdBy?.presignedProfilePicture}
									sx={{ width: 30, height: 30, marginLeft: "5px" }}
								/>
							)}
						</Box>
						{!isNextMessageFromSameUserAndTime && (
							<Box key={msg._id} className={`mb-4 flex ${isCurrentUser ? "justify-end" : "justify-start"}`}>
								<Typography variant="caption" className="text-xs mt-2 block !font-sans px-10">
									{formattedDate}
								</Typography>
							</Box>
						)}
						<Box ref={messagesEndRef} />
					</React.Fragment>
				);
			})}
			{selectedRow?.status !== "PENDING" && <EndMessage status={selectedRow?.status} resolvingUser={selectedRow?.resolvingUser} />}
			{showMessageSkeleton && <MessageSkeleton />}
			{!hideOpenWashout && (
				<WashoutDialog
					openPopper={openPopper}
					alertMessage={alertMessage}
					attachedFilesLength={attachedFilesLength}
					userId={userId}
					locationId={locationId}
					commissionCycleId={commissionCycleId}
					payPeriodId={payPeriodId}
					position={position}
					canEditWashout={canEditWashout}
				/>
			)}
		</Box>
	);
};

const EndMessage = ({
	status = "",
	resolvingUser,
}: {
	status?: string;
	resolvingUser?: {
		_id: string;
		fullName: string;
	};
}) => {
	return (
		<Box className="flex items-center justify-center">
			<Divider className="w-[100px]" />
			<Box className="flex flex-col justify-center items-center px-[20px]">
				<Typography variant="caption" className="text-xs !font-sans" sx={{ lineHeight: "15px" }}>
					Change Request has been {status.toLowerCase()} by
				</Typography>
				<Typography variant="caption" className="text-xs  !font-sans" sx={{ lineHeight: "15px" }}>
					{resolvingUser?.fullName}
				</Typography>
			</Box>
			<Divider className="w-[100px]" />
		</Box>
	);
};

type TAttachmentProps = {
	requestId: string | null;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	attachment: { _id: string; name: string; displayName: string; size: number; type: string };
};

const Attachments = (props: TAttachmentProps) => {
	const { locationId, commissionCycleId, payPeriodId, attachment } = props;
	const requestId = props.requestId ?? "";
	const fileName = attachment.displayName ?? "";
	const fileExtension = fileName.split(".").pop() as TAttachmentType;

	const { refetch, isFetching } = useQuery({
		queryKey: [
			"getChatAttachmentsPresignedUrl",
			locationId,
			commissionCycleId,
			payPeriodId,
			requestId,
			fileName,
			fileExtension,
			attachment._id,
		],
		queryFn: async () => {
			const result = await getChatAttachmentsPresignedUrl({
				locationId,
				commissionCycleId,
				payPeriodId,
				requestId,
				file: attachment,
				action: "getObject",
			});
			window.open(result.presignedUrl, "_blank", "noopener,noreferrer");
			return result;
		},
		staleTime: Infinity,
		enabled: false, // Don't fetch data on component mount
	});

	return (
		<Box position="relative">
			<Box
				className="flex justify-start items-center bg-[#f7f7f7] pl-1 pr-2 py-2 m-1 rounded-md shadow-md hover:cursor-pointer"
				onClick={() => refetch()}
				style={{ filter: isFetching ? "blur(2px)" : "none" }} // Adds blur effect when loading
			>
				<GetIconByAttachmentType type={fileExtension} />
				<Typography noWrap variant="body1" className="!ml-1 !font-sans text-default" title={fileName}>
					{fileName}
				</Typography>
			</Box>
			{isFetching && (
				<Box
					position="absolute"
					top={0}
					left={0}
					width="100%"
					height="100%"
					display="flex"
					justifyContent="center"
					alignItems="center"
					className="rounded-md"
				>
					<SpinnerSvg className="animate-[spin_3s_linear_infinite] text-gray-900" />;
				</Box>
			)}
		</Box>
	);
};

export default ChatMessages;
